import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';
import { addressBookType, addressTypes, cartTypeConstants } from '@elab/core/utils/constants';
import { selectors } from '@elab/core/store/cart';
import { selectors as userSelectors } from '@elab/core/store/user';
import { selectors as orderSelectors } from '@elab/core/store/order';
import {
    actions as storeActions,
    selectors as storeSelectors,
} from '@elab/core/store/store-details';
import {
    actions as checkoutAction,
    selectors as checkoutSelectors,
} from '@elab/core/store/checkout';
import { actions as addressActions } from '@elab/core/store/address';
import { selectors as paymentSelectors } from '@elab/core/store/payment';
import { getActions } from '@elab/components/connected/forms/CheckoutAddressForms/redux';
import isEmpty from '@elab/core/utils/isEmpty';
import connect from '@elab/core/store/connect';
import * as storage from '@elab/core/utils/storage';
import { checkEgiftcard } from '@elab/core/utils/egiftcard';

/**
 * Component is used to show delivery address information
 *
 * @attr {string} [section-title] - The name of section es. Pick up address
 * @attr {boolean} [step-payment = false] - Different title style if you stay in payment step
 * @attr {boolean} [ allowDeliveryAsBilling = false] - Used by PaymentAddress.js to handles delivery as billing
 * @attr {boolean} [ showDeliveryIcon = false] - Used to show configured icon near the address
 * @attr {boolean} [ showMobileNumber = false] - Used to show or hide the mobile phone number
 * @attr {string} [ deliveryModeIconUrl = ''] - get url for configured icon
 *
 * @param {object} [deliveryAddress = {}] - Billing Address information
 *
 * HTML Structure
 * <!-- Example -->
 * <e2-delivery-address
 *      section-title="Delivery address"
 *      step-payment
 * ></e2-delivery-address>
 */
const { DIGITAL } = cartTypeConstants;
export class DeliveryAddress extends LightLitElement {
    static get properties() {
        return {
            sectionTitle: { type: String, attribute: 'section-title' },
            stepPayment: { type: Boolean, attribute: 'step-payment' },
            cssClass: { type: String, attribute: 'css-class' },
            orderCode: { type: String, attribute: 'order-code' },
            offlineOrder: { type: Boolean, attribute: 'offline-order' },
            separator: { type: String, attribute: 'separator' },
            showStoreOpeningHour: { type: Boolean, attribute: 'show-store-opening-hour' },
            storeOpenLabel: { type: String, attribute: 'store-open-label' },
            storeClosedLabel: { type: String, attribute: 'store-closed-label' },
            deliveryAddress: { type: Object },
            defaultAddress: { type: Object },
            getProfile: { type: Object },
            hasLoyalty: { type: Boolean },

            storeUid: { type: String, attribute: 'store-code' },
            storeDistance: { type: Boolean, attribute: 'store-distance' },
            useNeighborhood: { type: Boolean, attribute: 'use-neighborhood' },
            store: { type: Object },
            cartGuid: { type: String },
            isHourTableRendered: { type: Boolean, attribute: false },
            // used in PaymentAddress
            allowDeliveryAsBilling: {
                type: Boolean,
                attribute: 'allow-delivery-as-billing',
            },
            deliveryModeIconUrl: { type: String, attribute: false },
            showDeliveryIcon: { type: Boolean, attribute: 'show-delivery-icon' },
            orderDetailAddress: {
                type: Boolean,
                attribute: 'order-detail-address',
            },
            separatorFormatter: { type: Boolean, attribute: 'separator-formatter' },
            individualAddressLabel: {
                type: String,
                attribute: 'individual-address-label',
            },
            corporateAddressLabel: {
                type: String,
                attribute: 'corporate-address-label',
            },
            showMobileNumber: { type: Boolean, attribute: 'show-mobile-number' },
            showEmail: { type: Boolean, attribute: 'show-email' },
            emailLabel: { type: String, attribute: 'email-label' },
            eGiftCardConfigurationInfos: { type: Array, attribute: false },
            alwaysShowDeliveryMode: { type: Boolean, attribute: 'always-show-delivery-mode' },
            editAddressText: { type: String, attribute: 'edit-address-text' },
            isPickupStore: { type: Boolean },
            showCorporateOptionFields: { type: Boolean, attribute: false },
            neighborhoodFieldInfo: { type: String, attribute: 'neighborhood-field-info' },
        };
    }

    constructor() {
        super();
        this.separator = '';
        this.cssClass = '';
        this.storeCode = '';
        this.cartGuid = '';
        this.storeDistance = false;
        this.showStoreOpeningHour = false;
        this.storeOpenLabel = '';
        this.storeClosedLabel = '';
        this.isHourTableRendered = false;
        this.deliveryModeIconUrl = '';
        this.showDeliveryIcon = false;
        this.orderDetailAddress = false;
        this.separatorFormatter = false;
        this.individualAddressLabel = '';
        this.corporateAddressLabel = '';
        this.showMobileNumber = false;
        this.showEmail = false;
        this.offlineOrder = false;
        this.alwaysShowDeliveryMode = false;
        this.emailLabel = '';
        this.eGiftCardConfigurationInfos = [];
        this.isPickupStore = false;
        this.showCorporateOptionFields = false;
        this.editAddressText = '';
        this.neighborhoodFieldInfo = '';
    }

    afterConnect() {
        if (this.orderCode) {
            this.dispatchEvent(new CustomEvent('load-order'));
        }
        if (this.storeUid) {
            this.dispatchEvent(new CustomEvent('select-store', { detail: this.storeUid }));
        }
    }

    updated() {
        if (this.deliveryAddress) {
            this.dispatchEvent(new CustomEvent('on-load'));
        }
    }

    firstUpdated() {
        super.firstUpdated();
        if (this.useNeighborhood) {
            this.dispatchEvent(
                new CustomEvent('update-neighborhood-field-info', {
                    detail: this.neighborhoodFieldInfo,
                }),
            );
        }
    }

    get isLoggedIn() {
        return !(this.getProfile && this.getProfile.anonymous);
    }

    get isDeliveryAddressEmpty() {
        return isEmpty(this.deliveryAddress);
    }

    get isNoDeliveryAddressNoDigitalCart() {
        return this.isDeliveryAddressEmpty && this.cartType !== DIGITAL;
    }

    get isNoDeliveryAddressWithDigitalCart() {
        return this.isDeliveryAddressEmpty && this.cartType === DIGITAL;
    }

    get eGiftcard() {
        return checkEgiftcard(this.eGiftCardConfigurationInfos);
    }

    getstoreDistanceTemplate() {
        const distanceData = storage.get('pickup-store-distance', true);
        if (distanceData && this.store.name && this.cartGuid) {
            if (distanceData.cart === this.cartGuid && distanceData.store === this.store.name) {
                return html`
                    <span class="address__pickup-store--distance__value">
                        ${distanceData.distance.toString().replace('.', ',')}
                    </span>
                    <span class="address__pickup-store--distance__measure">
                        km
                    </span>
                `;
            }
        }
        return html``;
    }

    renderRow = (value, className = 'company') => {
        return value
            ? html`
                  <p class="address__row address__row--${className}">
                      ${value}
                  </p>
              `
            : '';
    };

    getStoreName(addressType) {
        if (addressType === addressBookType.SHIPPING_ADDRESS) {
            return 'shippingAddress';
        }
        if (addressType === addressBookType.BILLING_ADDRESS) {
            return 'billingAddress';
        }
    }

    handleEdit(event, id, addressType) {
        event.stopPropagation();
        const storeName = this.getStoreName(addressType);

        /*
            Below parameters such as radioView, useCartUrlForEdit, SubmitMethod,
            selectForPaymentAfterEditing are not currently used as per current
            requirement but still keeping it for future changes
        */

        this.dispatchEvent(
            new CustomEvent('edit-address-in-popup', {
                detail: {
                    id,
                    storeName,
                    addressType,
                    radioView: false,
                    useCartUrlForEdit: false,
                    useCartUrlForAdd: false,
                    submitEditMethod: '',
                    selectForPaymentAfterEditing: false,
                },
            }),
        );
    }

    renderAddress(address) {
        const {
            firstName = '',
            middleName = '',
            lastName = '',
            postalCode = '',
            line1,
            line2,
            pobox,
            town,
            formattedPhone = '',
            mobileNumber = '',
            country = {},
            billingAddress,
            contactAddress,
            shippingAddress,
            companyName = '',
            addressType,
            companyCode,
            vatNumber,
            iban,
            bankName,
            neighborhood = '',
            id = null,
        } = address;
        const isCorporateAddress = addressType === addressTypes.CORPORATE;
        const showCorporateAddress = isCorporateAddress && this.showCorporateOptionFields;
        const pickupAddress = this.storeUid && this.store;

        const title = pickupAddress
            ? !this.store.displayName
                ? this.store.name
                : this.store.displayName
            : address.title || '';

        return html`
            <div class="address__current-address ${this.cssClass}">
                <h4 class="address__title${this.stepPayment ? '--payment' : ''}">
                    ${this.sectionTitle}
                </h4>

                <p class="address__row">
                    <v-slot></v-slot>
                    ${pickupAddress && this.storeDistance
                        ? html`
                              <span class="address__pickup-store--distance">
                                  ${this.getstoreDistanceTemplate()}
                              </span>
                          `
                        : html``}
                </p>
                ${!(this.isPickupStore && this.isPickupStore.length)
                    ? !this.offlineOrder
                        ? html``
                        : html`
                              <p class="address__row address__row--name">
                                  <span class="address__item address__item--first-name">
                                      ${firstName}
                                  </span>
                              </p>
                          `
                    : html`
                          <p class="address__row address__row--name">
                              <span class="address__item address__item--title">
                                  ${title}
                              </span>
                              <span class="address__item address__item--first-name">
                                  ${firstName}
                              </span>
                          </p>
                      `}
                ${!billingAddress && !contactAddress && !shippingAddress && !pickupAddress
                    ? html``
                    : showCorporateAddress
                    ? html`
                          <p class="address__row address__row--name">
                              ${companyName}
                          </p>
                      `
                    : html`
                          <p class="address__row address__row--name">
                              <span class="address__item address__item--title">
                                  ${title}
                              </span>
                              ${!pickupAddress
                                  ? html`
                                        <span class="address__item address__item--first-name">
                                            ${firstName}
                                        </span>
                                        ${middleName
                                            ? html`
                                                  <span
                                                      class="address__item address__item--middle-name"
                                                  >
                                                      ${middleName}
                                                  </span>
                                              `
                                            : html``}
                                        <span class="address__item"
                                            >${lastName + this.separator}</span
                                        >
                                    `
                                  : html``}
                          </p>
                      `}
                ${this.corporateAddressLabel && this.individualAddressLabel
                    ? html`
                          <p class="address__row address__row--street">
                              ${companyName
                                  ? this.corporateAddressLabel
                                  : this.individualAddressLabel}
                          </p>
                      `
                    : ''}
                <p class="address__row address__row--street">
                    <span class="address__line address__line--street">
                        ${this.separatorFormatter
                            ? line1
                                ? line1 + this.separator
                                : ''
                            : line1 || ''}
                    </span>
                    <span class="address__line address__line--number">
                        ${this.separatorFormatter
                            ? line2 || ''
                            : line2
                            ? line2 + this.separator
                            : ''}
                    </span>
                    <span class="address__line address__line--pobox">
                        ${pobox ? pobox + this.separator : ''}
                    </span>
                </p>

                ${neighborhood && this.useNeighborhood
                    ? html`
                          <p class="address__row address__row--neighborhood">
                              ${neighborhood}
                          </p>
                      `
                    : null}
                <p class="address__row address__row--postalcode">
                    ${this.separatorFormatter ? postalCode + this.separator : postalCode}
                </p>
                <p class="address__row address__row--town">
                    ${this.separatorFormatter ? town || '' : town ? town + this.separator : ''}
                </p>
                <p class="address__row address__row--country">
                    ${country.name || ''}
                </p>

                ${formattedPhone !== ''
                    ? html`
                          <p class="address__row address__row--phone">
                              ${formattedPhone}
                          </p>
                      `
                    : html``}
                ${showCorporateAddress ? null : this.renderRow(companyName)}
                ${this.showMobileNumber
                    ? html`
                          <p class="address__row address__row--mobile">
                              ${mobileNumber}
                          </p>
                      `
                    : html``}
                ${showCorporateAddress
                    ? html`
                          ${this.renderRow(companyCode)} ${this.renderRow(vatNumber)}
                          ${this.renderRow(iban)} ${this.renderRow(bankName)}
                      `
                    : null}
                ${this.showEmail && this.eGiftcard !== null
                    ? html`
                          <p class="address__row address__row--email">
                              ${this.emailLabel} ${this.eGiftcard.recipientEmail}
                          </p>
                      `
                    : ''}
                ${this.renderStoreOpeningHourLabel()} ${this.renderHourTable()}
                ${shippingAddress &&
                this.useNeighborhood &&
                !this.isPaymentPage &&
                !this.allowDeliveryAsBilling &&
                !this.orderDetailAddress &&
                this.isLoggedIn
                    ? html`
                          <div class="address__row address-item__link address__row--edit-address">
                              <a
                                  @click="${e =>
                                      this.handleEdit(e, id, addressBookType.SHIPPING_ADDRESS)}"
                              >
                                  ${this.editAddressText}
                              </a>
                          </div>
                      `
                    : null}

                <v-slot name="append"></v-slot>
            </div>
        `;
    }

    getAddressTemplate(address) {
        return this.showDeliveryIcon
            ? html`
                  <div class="delivery-address">
                      <div class="delivery-icon">
                          <img src="${this.deliveryModeIconUrl}" />
                      </div>
                      ${this.renderAddress(address)}
                  </div>
              `
            : this.renderAddress(address);
    }

    renderStoreOpeningHourLabel() {
        return this.showStoreOpeningHour && !this.isDeliveryModeHome
            ? html`
                  <div
                      class="address__row address__row--opening-hour"
                      @click="${() => (this.isHourTableRendered = !this.isHourTableRendered)}"
                  >
                      ${this.storeOpenLabel}
                  </div>
              `
            : html``;
    }

    renderHourTable() {
        return this.isHourTableRendered
            ? html`
                  <e2-hours-table
                      v-slot="content"
                      .openHours="${this.store.openingHours.weekDayOpeningList || []}"
                      closed-label="${this.storeClosedLabel}"
                  ></e2-hours-table>
              `
            : html``;
    }

    render() {
        if (this.useDeliveryAsBilling && this.useNeighborhood) {
            this.dispatchEvent(
                new CustomEvent('update-shippingAdress-as-billingAddress', {
                    detail: this.deliveryAddress,
                }),
            );
        }
        const showDeliveryModeEmail =
            this.showEmail && this.isDeliveryModeEmail && this.eGiftcard !== null;
        const checkIfDeliveryAddressHasEmpty = this.deliveryAddress && this.deliveryAddress.empty;
        if (
            !showDeliveryModeEmail &&
            (this.isDeliveryAddressEmpty ||
                checkIfDeliveryAddressHasEmpty ||
                this.hasLoyalty ||
                (this.allowDeliveryAsBilling &&
                    !this.deliveryAddress.billingAddress &&
                    !this.deliveryAddress.shippingAddress))
        ) {
            return this.alwaysShowDeliveryMode ? this.getAddressTemplate({}) : html``;
        }

        if (this.isNoDeliveryAddressNoDigitalCart) {
            return this.getAddressTemplate({});
        }
        if (this.isNoDeliveryAddressWithDigitalCart) {
            return this.getAddressTemplate(this.defaultAddress);
        }
        return this.getAddressTemplate(this.deliveryAddress);
    }
}

const mapDispatchToEvents = dispatch => ({
    'select-store': ({ detail }) => dispatch(storeActions.getStoreDetails(detail)),
    'edit-address-in-popup': ({
        detail: {
            id,
            storeName,
            addressType,
            radioView,
            useCartUrlForEdit,
            useCartUrlForAdd,
            submitEditMethod,
            selectForPaymentAfterEditing,
        },
    }) =>
        dispatch(
            getActions(storeName).openFormInDialog({
                id,
                addressType,
                radioView,
                useCartUrlForEdit,
                useCartUrlForAdd,
                submitEditMethod,
                selectForPaymentAfterEditing,
            }),
        ),
    'update-shippingAdress-as-billingAddress': ({ detail }) =>
        dispatch(checkoutAction.setAddressForBilling(detail)),
    'update-neighborhood-field-info': ({ detail }) =>
        dispatch(addressActions.setNeighborhoodFieldInfo(detail)),
});

const mapStateToProps = (state, ctx) => ({
    deliveryAddress: ctx.orderDetailAddress
        ? orderSelectors.getDeliveryAddress(state)
        : selectors.getDeliveryAddress(state),
    defaultAddress: userSelectors.getDefaultAddress(state),
    getProfile: userSelectors.getProfile(state),
    cartType: selectors.getCartType(state),
    store: storeSelectors.getDataStore(state),
    cartGuid: selectors.getGuid(state) || '',
    isDeliveryModeHome: selectors.isDeliveryModeHome(state),
    isDeliveryModeEmail: selectors.isDeliveryModeEmail(state),
    deliveryModeIconUrl: selectors.getDeliveryModeIconUrl(state),
    eGiftCardConfigurationInfos: selectors.getEGiftCardConfigurationInfos(state),
    useDeliveryAsBilling: checkoutSelectors.getUseDeliveryAsBilling(state),
    isPaymentPage: paymentSelectors.getIsPaymentPage(state),
});

export default () =>
    customElements.define(
        'e2-delivery-address',
        connect(mapStateToProps, mapDispatchToEvents)(DeliveryAddress),
    );
